import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

interface ChartData {
  name: string;
  value: number;
  color?: string;
}

interface ChartProps {
  title: string;
  description?: string;
  data: ChartData[];
  type?: 'bar' | 'line' | 'pie' | 'area';
  height?: number;
}

const SimpleBarChart: React.FC<{ data: ChartData[]; height?: number }> = ({ data, height = 200 }) => {
  const maxValue = Math.max(...data.map(d => d.value));
  
  return (
    <div className="w-full" style={{ height }}>
      <div className="flex items-end justify-between h-full gap-2">
        {data.map((item, index) => (
          <div key={index} className="flex flex-col items-center flex-1">
            <div
              className="w-full bg-primary rounded-t-sm transition-all duration-300 hover:bg-primary/80"
              style={{
                height: `${(item.value / maxValue) * (height - 40)}px`,
                backgroundColor: item.color || 'hsl(var(--primary))'
              }}
              title={`${item.name}: ${item.value}`}
            />
            <span className="text-xs text-muted-foreground mt-2 text-center">
              {item.name}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const SimpleLineChart: React.FC<{ data: ChartData[]; height?: number }> = ({ data, height = 200 }) => {
  const maxValue = Math.max(...data.map(d => d.value));
  const minValue = Math.min(...data.map(d => d.value));
  const range = maxValue - minValue;
  
  const points = data.map((item, index) => {
    const x = (index / (data.length - 1)) * 100;
    const y = 100 - ((item.value - minValue) / range) * 100;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <div className="w-full" style={{ height }}>
      <svg width="100%" height="100%" viewBox="0 0 100 100" className="overflow-visible">
        <polyline
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth="0.5"
          points={points}
          className="transition-all duration-300"
        />
        {data.map((item, index) => {
          const x = (index / (data.length - 1)) * 100;
          const y = 100 - ((item.value - minValue) / range) * 100;
          return (
            <circle
              key={index}
              cx={x}
              cy={y}
              r="1"
              fill="hsl(var(--primary))"
              className="transition-all duration-300 hover:r-2"
            />
          );
        })}
      </svg>
    </div>
  );
};

const SimplePieChart: React.FC<{ data: ChartData[]; height?: number }> = ({ data, height = 200 }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let cumulativePercentage = 0;
  
  return (
    <div className="w-full flex items-center justify-center" style={{ height }}>
      <svg width={height} height={height} viewBox="0 0 100 100" className="overflow-visible">
        {data.map((item, index) => {
          const percentage = (item.value / total) * 100;
          const startAngle = (cumulativePercentage / 100) * 360;
          const endAngle = ((cumulativePercentage + percentage) / 100) * 360;
          
          const startAngleRad = (startAngle * Math.PI) / 180;
          const endAngleRad = (endAngle * Math.PI) / 180;
          
          const x1 = 50 + 40 * Math.cos(startAngleRad);
          const y1 = 50 + 40 * Math.sin(startAngleRad);
          const x2 = 50 + 40 * Math.cos(endAngleRad);
          const y2 = 50 + 40 * Math.sin(endAngleRad);
          
          const largeArcFlag = percentage > 50 ? 1 : 0;
          
          const pathData = [
            `M 50 50`,
            `L ${x1} ${y1}`,
            `A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2}`,
            'Z'
          ].join(' ');
          
          cumulativePercentage += percentage;
          
          return (
            <path
              key={index}
              d={pathData}
              fill={item.color || `hsl(${index * 60}, 70%, 50%)`}
              className="transition-all duration-300 hover:opacity-80"
            />
          );
        })}
      </svg>
    </div>
  );
};

const Chart: React.FC<ChartProps> = ({ title, description, data, type = 'bar', height = 200 }) => {
  const renderChart = () => {
    switch (type) {
      case 'line':
        return <SimpleLineChart data={data} height={height} />;
      case 'pie':
        return <SimplePieChart data={data} height={height} />;
      case 'bar':
      default:
        return <SimpleBarChart data={data} height={height} />;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        {renderChart()}
        <div className="mt-4 flex flex-wrap gap-2">
          {data.map((item, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: item.color || `hsl(${index * 60}, 70%, 50%)` }}
              />
              <span>{item.name}: {item.value}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export const Charts: React.FC<{ payments?: any[]; analyticsData?: any }> = ({ payments = [], analyticsData }) => {
  // Generate chart data from payments
  const paymentVolumeByCountry = payments.reduce((acc, payment) => {
    const country = payment.toCountry || 'Unknown';
    acc[country] = (acc[country] || 0) + (payment.amount || 0);
    return acc;
  }, {} as Record<string, number>);

  const paymentStatusData = payments.reduce((acc, payment) => {
    const status = payment.status || 'unknown';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const monthlyData = Array.from({ length: 12 }, (_, i) => {
    const month = new Date(2024, i, 1).toLocaleDateString('en-US', { month: 'short' });
    const value = Math.floor(Math.random() * 10000) + 5000; // Mock data
    return { name: month, value };
  });

  return (
    <div className="space-y-6">
      <Tabs defaultValue="volume" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="volume">Volume by Country</TabsTrigger>
          <TabsTrigger value="status">Payment Status</TabsTrigger>
          <TabsTrigger value="monthly">Monthly Trends</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>
        
        <TabsContent value="volume" className="space-y-4">
          <Chart
            title="Payment Volume by Country"
            description="Total payment volume distributed by destination country"
            data={Object.entries(paymentVolumeByCountry).map(([country, volume]) => ({
              name: country,
              value: Number(volume),
              color: `hsl(${Math.random() * 360}, 70%, 50%)`
            }))}
            type="bar"
            height={300}
          />
        </TabsContent>
        
        <TabsContent value="status" className="space-y-4">
          <Chart
            title="Payment Status Distribution"
            description="Current distribution of payment statuses"
            data={Object.entries(paymentStatusData).map(([status, count]) => ({
              name: status,
              value: Number(count),
              color: status === 'completed' ? '#10b981' : 
                     status === 'pending' ? '#f59e0b' : 
                     status === 'failed' ? '#ef4444' : '#6b7280'
            }))}
            type="pie"
            height={300}
          />
        </TabsContent>
        
        <TabsContent value="monthly" className="space-y-4">
          <Chart
            title="Monthly Payment Trends"
            description="Payment volume trends over the past 12 months"
            data={monthlyData}
            type="line"
            height={300}
          />
        </TabsContent>
        
        <TabsContent value="analytics" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Chart
              title="Success Rate"
              description="Payment success rate over time"
              data={[
                { name: 'Success', value: 85, color: '#10b981' },
                { name: 'Failed', value: 15, color: '#ef4444' }
              ]}
              type="pie"
              height={250}
            />
            <Chart
              title="Processing Time"
              description="Average processing time by status"
              data={[
                { name: 'Fast', value: 60, color: '#10b981' },
                { name: 'Medium', value: 30, color: '#f59e0b' },
                { name: 'Slow', value: 10, color: '#ef4444' }
              ]}
              type="bar"
              height={250}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Charts;
